import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Table, Button, Modal, Space, Input, InputNumber, Select, Form, message, Switch, Tag, Tooltip } from 'antd';
import { PlusOutlined, ReloadOutlined, CopyOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import request from '../../../utils/request';
import { getAdminModuleAccessApplications, reviewModuleAccessApplication, ModuleAccessApplication } from '../../../services/moduleAccessApplication';
import { getCustomerRiskProfile, saveCustomerRiskProfile, DEFAULT_CUSTOMER_RISK } from '../../../services/logistics';

interface CustomerRow {
  id: string;
  username: string;
  email?: string;
  customer_code?: string;
  company_name?: string;
  personal_name?: string;
  main_product_categories?: string[];
  sales_platform_links?: string[];
  phone_country_code?: string;
  phone_number?: string;
  enabled_business_modules?: string[];
  completed_verifications_labels?: string[];
  website?: string;
  notes?: string;
  social_accounts?: Record<string, string>;
  timezone?: string;
  last_login?: string;
  last_login_ip?: string;
  last_login_ip_region?: string;
  customer_sub_role?: string;
  is_active?: boolean;
  parent_username?: string;
  pending_module_access_applications_count?: number;
  module_access_applications_summary?: Array<{
    module_key: string;
    module_label: string;
    status: 'pending' | 'approved' | 'rejected' | string;
    status_label: string;
    updated_at?: string;
  }>;
}

const AdminCustomerList: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<CustomerRow[]>([]);
  const [detailVisible, setDetailVisible] = useState(false);
  const [current, setCurrent] = useState<CustomerRow | null>(null);
  const [pagination, setPagination] = useState<{ current: number; pageSize: number; total: number }>({ current: 1, pageSize: 20, total: 0 });
  // 直接从 URL 的初始值里同步计算出第一次渲染就该生效的筛选条件（而不是靠挂载后的 useEffect
  // 事后合并进来）——否则组件挂载时会先用“默认筛选”发出第一次请求，紧接着才被 effect
  // 追加 sub_role 触发第二次请求，两个请求谁先返回就用谁的结果覆盖表格，而“账号角色”下拉框
  // 只反映 state、不反映实际显示的是哪次请求的数据，导致下拉框显示“主账号”但列表仍是全部账号。
  const [filters, setFilters] = useState<{ q?: string; customer_code?: string; company_name?: string; username?: string; sub_role?: string; is_active?: string }>(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('pending_review') === '1') return {};
    const subRoleRaw = String(params.get('sub_role') || '').trim();
    const subRole = (subRoleRaw === 'main_account' || subRoleRaw === 'sub_account') ? subRoleRaw : undefined;
    return { is_active: 'true', ...(subRole ? { sub_role: subRole } : {}) };
  });
  const [pwdVisible, setPwdVisible] = useState(false);
  const [pwdUser, setPwdUser] = useState<CustomerRow | null>(null);
  const [applicationVisible, setApplicationVisible] = useState(false);
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [applicationRows, setApplicationRows] = useState<ModuleAccessApplication[]>([]);
  const [lastLoginVisible, setLastLoginVisible] = useState(false);
  const [lastLoginRecord, setLastLoginRecord] = useState<CustomerRow | null>(null);
  const [reviewingId, setReviewingId] = useState<number | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [reviewAction, setReviewAction] = useState<'approve' | 'reject'>('approve');
  const [form] = Form.useForm();
  const [riskVisible, setRiskVisible] = useState(false);
  const [riskLoading, setRiskLoading] = useState(false);
  const [riskSaving, setRiskSaving] = useState(false);
  const [riskCurrent, setRiskCurrent] = useState<CustomerRow | null>(null);
  const [riskForm] = Form.useForm();
  const [pendingApplicationOnly, setPendingApplicationOnly] = useState(() => new URLSearchParams(location.search).get('pending_review') === '1');
  const fromDashboardPendingReview = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('pending_review') === '1';
  }, [location.search]);
  const fromDashboardSubRole = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    const raw = String(params.get('sub_role') || '').trim();
    return raw === 'main_account' || raw === 'sub_account' ? raw : '';
  }, [location.search]);

  const hasPendingApplication = (row: CustomerRow) => {
    const summary = Array.isArray(row.module_access_applications_summary) ? row.module_access_applications_summary : [];
    return Number(row.pending_module_access_applications_count || 0) > 0 || summary.some((item) => item?.status === 'pending');
  };

  const openExternal = (url?: string): string | null => {
    const text = String(url || '').trim();
    if (!text) return null;
    if (/^https?:\/\//i.test(text)) return text;
    return `http://${text}`;
  };

  const copyText = async (text: string, successMsg = '复制成功') => {
    try {
      await navigator.clipboard.writeText(text);
      message.success(successMsg);
    } catch {
      message.error('复制失败，请检查浏览器权限');
    }
  };

  // 请求序号保护：筛选条件短时间内变化多次时，网络请求的返回顺序不一定和发出顺序一致，
  // 只有"当前仍是最新一次发出的请求"的响应才允许写入 data/pagination，避免旧请求的结果
  // 在新请求之后才返回、反而覆盖了新请求已经渲染出的正确结果。
  const fetchSeqRef = useRef(0);
  const fetchPage = async (page: number, pageSize: number) => {
    const seq = (fetchSeqRef.current += 1);
    setLoading(true);
    try {
      const requestPage = pendingApplicationOnly ? 1 : page;
      const requestPageSize = pendingApplicationOnly ? 500 : pageSize;
      const resp = await request.get('/api/v1/users/', {
        params: { role: 'customer', page: requestPage, page_size: requestPageSize, ...filters },
      });
      if (seq !== fetchSeqRef.current) return;
      const body = resp.data;
      const items: any[] = Array.isArray(body) ? body : (Array.isArray(body?.results) ? body.results : []);
      const rows: CustomerRow[] = items.map((u: any) => ({
        id: String(u.id),
        username: u.username,
        email: u.email,
        customer_code: u.customer_code,
        company_name: u.company_name,
        personal_name: u.personal_name,
        main_product_categories: Array.isArray(u.main_product_categories) ? u.main_product_categories : [],
        sales_platform_links: Array.isArray(u.sales_platform_links) ? u.sales_platform_links : [],
        phone_country_code: u.phone_country_code,
        phone_number: u.phone_number,
        enabled_business_modules: Array.isArray(u.enabled_business_modules) ? u.enabled_business_modules : [],
        completed_verifications_labels: Array.isArray(u.completed_verifications_labels) ? u.completed_verifications_labels : [],
        website: u.website,
        notes: u.notes,
        social_accounts: u.social_accounts || {},
        timezone: u.timezone,
        last_login: u.last_login,
        last_login_ip: u.last_login_ip,
        last_login_ip_region: u.last_login_ip_region,
        customer_sub_role: u.customer_sub_role,
        is_active: u.is_active,
        // 兼容多种后端返回结构，尽量取到父主账号用户名
        parent_username: (
          u.parent_username ||
          u.parent_customer_username ||
          (u.parent_customer && (u.parent_customer.username || u.parent_customer_name)) ||
          (u.profile && (u.profile.parent_customer_username || (u.profile.parent_customer && u.profile.parent_customer.username)))
        ),
        pending_module_access_applications_count: Number(u.pending_module_access_applications_count || 0),
        module_access_applications_summary: Array.isArray(u.module_access_applications_summary)
          ? u.module_access_applications_summary
          : [],
      }));
      const finalRows = pendingApplicationOnly ? rows.filter(hasPendingApplication) : rows;
      const total = pendingApplicationOnly
        ? finalRows.length
        : (typeof body?.count === 'number' ? body.count : finalRows.length);
      setData(finalRows);
      setPagination(prev => ({ ...prev, current: pendingApplicationOnly ? 1 : page, pageSize, total }));
    } catch (e) {
      if (seq !== fetchSeqRef.current) return;
      setData([]);
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    fetchPage(1, pagination.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pendingApplicationOnly]);

  // 工作台快捷跳转：pending_review=1 时自动打开“有权限申请”开关并清空旧筛选
  useEffect(() => {
    if (!fromDashboardPendingReview) return;
    setPendingApplicationOnly(true);
    setFilters((prev) => {
      const hasAnyFilter = Object.values(prev || {}).some((v) => String(v || '').trim() !== '');
      if (!hasAnyFilter) return prev;
      return {};
    });
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [fromDashboardPendingReview]);

  // 工作台快捷跳转：支持通过 query 预设“账号角色”筛选。初始值已经在上面 filters 的懒初始化里
  // 处理过了，这里只需要处理“组件没有重新挂载、但 URL 的 sub_role 又变了”这种后续跳转场景——
  // 值没变时原样返回 prev（保持同一个引用），避免触发一次内容完全相同、纯浪费的重复请求。
  useEffect(() => {
    if (!fromDashboardSubRole) return;
    setFilters((prev) => (prev.sub_role === fromDashboardSubRole ? prev : { ...prev, sub_role: fromDashboardSubRole }));
    setPagination((prev) => ({ ...prev, current: 1 }));
  }, [fromDashboardSubRole]);

  const columns: ColumnsType<CustomerRow> = [
    {
      title: '客户编码',
      dataIndex: 'customer_code',
      key: 'customer_code',
      width: 160,
      render: (v: string | undefined, r) => (
        <Space size={6}>
          <span>{v || ''}</span>
          {r.is_active === false ? <Tag color="red">已禁用</Tag> : null}
        </Space>
      ),
    },
    {
      title: '用户名',
      dataIndex: 'username',
      key: 'username',
      width: 150,
      render: (v?: string) => {
        const username = String(v || '').trim();
        if (!username) return '';
        return (
          <Space size={6}>
            <span>{username}</span>
            <Tooltip title="复制用户名">
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyText(username, '用户名已复制')}
                style={{ padding: 0, minWidth: 'auto' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: 'email',
      dataIndex: 'email',
      key: 'email',
      width: 210,
      render: (v?: string) => {
        const email = String(v || '').trim();
        if (!email) return '';
        return (
          <Space size={6}>
            <Tooltip title={email}>
              <span
                style={{
                  display: 'inline-block',
                  maxWidth: 150,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  verticalAlign: 'bottom',
                }}
              >
                {email}
              </span>
            </Tooltip>
            <Tooltip title="复制邮箱">
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                onClick={() => copyText(email, '邮箱已复制')}
                style={{ padding: 0, minWidth: 'auto' }}
              />
            </Tooltip>
          </Space>
        );
      },
    },
    {
      title: '账号角色',
      dataIndex: 'customer_sub_role',
      key: 'customer_sub_role',
      width: 96,
      render: (v?: string) => {
        if (v === 'main_account') return <Tag color="green">主账号</Tag>;
        if (v === 'sub_account') return <Tag color="orange">子账号</Tag>;
        return '-';
      },
    },
    { title: '所属主账号', dataIndex: 'parent_username', key: 'parent_username', width: 100, render: (_: any, r) => (r.customer_sub_role === 'sub_account' ? (r.parent_username || '') : '') },
    { title: '公司名称', dataIndex: 'company_name', key: 'company_name', width: 130 },
    {
      title: '权限申请',
      key: 'pending_applications',
      width: 130,
      render: (_: any, r) => {
        const summary = Array.isArray(r.module_access_applications_summary) ? r.module_access_applications_summary : [];
        if (summary.length === 0) return null;
        return (
          <div>
            {summary.map((item, idx) => {
              const color = item.status === 'approved' ? 'green' : item.status === 'rejected' ? 'red' : 'orange';
              return (
                <div key={`${item.module_key}-${idx}`} style={{ marginBottom: 4 }}>
                  <Tag color={color}>{item.module_label}：{item.status_label}</Tag>
                </div>
              );
            })}
          </div>
        );
      },
    },
    {
      title: '备注',
      dataIndex: 'notes',
      key: 'notes',
      width: 180,
      render: (v?: string) => {
        const text = String(v || '');
        if (!text) return '';
        if (text.length <= 40) return text;
        const brief = `${text.slice(0, 40)}...`;
        return (
          <Tooltip title={text} overlayStyle={{ maxWidth: 560 }}>
            <span>{brief}</span>
          </Tooltip>
        );
      },
    },
    {
      title: '操作',
      key: 'action',
      width: 320,
      fixed: 'right',
      render: (_, record) => {
        const summary = Array.isArray(record.module_access_applications_summary) ? record.module_access_applications_summary : [];
        const hasApplications = summary.length > 0;
        const hasPendingApplications =
          Number(record.pending_module_access_applications_count || 0) > 0
          || summary.some((item) => item?.status === 'pending');
        return (
          <Space wrap size={[8, 0]}>
            <Button type="link" onClick={() => { setCurrent(record); setDetailVisible(true); }}>
              查看详情
            </Button>
            <Button
              type="link"
              onClick={() => {
                setLastLoginRecord(record);
                setLastLoginVisible(true);
              }}
            >
              最后登录信息
            </Button>
            {hasApplications && (
              <Button
                type="link"
                danger={hasPendingApplications}
                style={hasPendingApplications ? { fontWeight: 700 } : undefined}
                onClick={() => openApplicationModal(record)}
              >
                权限申请审核
              </Button>
            )}
            <Button type="link" onClick={() => { setPwdUser(record); setPwdVisible(true); setTimeout(() => form.resetFields(), 0); }}>重置密码</Button>
            {record.customer_sub_role !== 'sub_account' && (
              <Button type="link" onClick={() => navigate(`/dashboard/admin/customers/${record.id}/sub-accounts`)}>
                子账号管理
              </Button>
            )}
            {record.customer_sub_role !== 'sub_account' ? (
              <Button type="link" onClick={() => openRiskModal(record)}>风险设置</Button>
            ) : (
              <Tooltip title="子账号购买运单记到主账号名下，风险设置跟随主账号，请在主账号行上调整">
                <Button type="link" disabled>风险设置</Button>
              </Tooltip>
            )}
            {record.is_active !== false ? (
              <Button type="link" danger onClick={() => handleToggleActive(record, false)}>禁用</Button>
            ) : (
              <Button type="link" onClick={() => handleToggleActive(record, true)}>启用</Button>
            )}
          </Space>
        );
      },
    },
  ];

  // 时间格式化（使用用户时区与管理员时区）
  const renderDualTimezone = (iso?: string, userTz?: string) => {
    if (!iso) return '-';
    try {
      const adminTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const fmt = (tz?: string) => {
        const d = new Date(iso);
        const s = new Intl.DateTimeFormat('zh-CN', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
          timeZone: tz || 'UTC'
        }).format(d).replaceAll('/', '-');
        return s;
      };
      return (
        <div>
          <div>{fmt(userTz)}</div>
          <div>{fmt(adminTz)}</div>
        </div>
      );
    } catch {
      return iso;
    }
  };

  const handleToggleActive = async (record: CustomerRow, nextActive: boolean) => {
    try {
      await request.patch(`/api/v1/users/${record.id}/`, { is_active: nextActive });
      message.success(nextActive ? '已启用' : '已禁用');
      fetchPage(pagination.current, pagination.pageSize);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '操作失败');
    }
  };

  // 子账号购买运单最终都记到主账号名下，没有独立的余额/保证金，风险设置只对主账号生效，
  // 打开这个弹窗时按钮本来就只在主账号行上出现，这里不用再判断一次。
  const openRiskModal = async (record: CustomerRow) => {
    setRiskCurrent(record);
    setRiskVisible(true);
    setRiskLoading(true);
    try {
      const resp = await getCustomerRiskProfile(record.id);
      riskForm.setFieldsValue({
        deposit_multiplier: Number(resp.data.deposit_multiplier),
        allow_international: resp.data.allow_international,
        remark: resp.data.remark,
      });
    } catch (e: any) {
      if (e?.response?.status === 404) {
        // 还没为这个客户建过档案，按系统默认值展示——不是报错。
        riskForm.setFieldsValue({
          deposit_multiplier: Number(DEFAULT_CUSTOMER_RISK.deposit_multiplier),
          allow_international: DEFAULT_CUSTOMER_RISK.allow_international,
          remark: '',
        });
      } else {
        message.error('获取客户风险档案失败');
      }
    } finally {
      setRiskLoading(false);
    }
  };

  const handleSaveRisk = async () => {
    if (!riskCurrent) return;
    try {
      const values = await riskForm.validateFields();
      setRiskSaving(true);
      await saveCustomerRiskProfile({
        customer: riskCurrent.id,
        deposit_multiplier: values.deposit_multiplier,
        allow_international: values.allow_international,
        remark: values.remark || '',
      });
      message.success('保存成功');
      setRiskVisible(false);
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.response?.data?.detail || '保存失败');
    } finally {
      setRiskSaving(false);
    }
  };

  const openApplicationModal = async (record: CustomerRow) => {
    try {
      setCurrent(record);
      setApplicationVisible(true);
      setApplicationLoading(true);
      const rows = await getAdminModuleAccessApplications({ applicant_id: record.id, status: 'all' });
      setApplicationRows(rows);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '获取申请列表失败');
      setApplicationRows([]);
    } finally {
      setApplicationLoading(false);
    }
  };

  const handleReview = async (row: ModuleAccessApplication, action: 'approve' | 'reject') => {
    try {
      setReviewingId(row.id);
      await reviewModuleAccessApplication(row.id, action, reviewNote || undefined);
      message.success(action === 'approve' ? '审核通过并已写入客户资料' : '已驳回申请');
      fetchPage(pagination.current, pagination.pageSize);
      setReviewAction('approve');
      setReviewNote('');
      setApplicationVisible(false);
      setApplicationRows([]);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '审核失败');
    } finally {
      setReviewingId(null);
    }
  };

  const buildApplicationCopyText = (row: ModuleAccessApplication) => {
    const lines: string[] = [];
    lines.push(`客户：${current?.username || '-'}`);
    lines.push(`服务板块：${row.module_label}`);
    lines.push(`状态：${row.status_label}`);
    lines.push(`公司名称：${row.payload?.company_name || '-'}`);
    lines.push(`公司网站：${row.payload?.website || '-'}`);
    lines.push(`个人姓名：${row.payload?.personal_name || '-'}`);
    lines.push(`主营产品类目：${(row.payload?.main_product_categories || []).join('；') || '-'}`);
    lines.push(`销售平台店铺链接或产品链接：${(row.payload?.sales_platform_links || []).join('；') || '-'}`);
    lines.push(`电话国家码：${row.payload?.phone_country_code || '-'}`);
    lines.push(`电话号码：${row.payload?.phone_number || '-'}`);
    if (Array.isArray(row.payload?.service_requirement_items) && row.payload.service_requirement_items.length > 0) {
      lines.push(`服务需求：${row.payload.service_requirement_items.join(' / ')}`);
    }
    lines.push(`服务需求补充描述：${String(row.payload?.service_requirement_description || '')
      .replace(/^服务需求：.*(?:\r?\n)?/, '')
      .replace(/^补充说明：/, '')
      .trim() || '-'}`);
    return lines.join('\n');
  };

  const DetailModal = () => (
    <Modal
      open={detailVisible}
      title="客户账号详情"
      onCancel={() => setDetailVisible(false)}
      footer={[
        <Button
          key="copy"
          onClick={() => {
            const lines: string[] = [];
            lines.push(`客户编码：${current?.customer_code || '-'}`);
            lines.push(`用户名：${current?.username || '-'}`);
            lines.push(`邮箱：${current?.email || '-'}`);
            lines.push(`账号角色：${current?.customer_sub_role === 'main_account' ? '主账号' : current?.customer_sub_role === 'sub_account' ? '子账号' : '-'}`);
            lines.push(`所属主账号：${current?.parent_username || '-'}`);
            lines.push(`公司名称：${current?.company_name || '-'}`);
            lines.push(`个人姓名：${current?.personal_name || '-'}`);
            lines.push(`公司网站：${current?.website || '-'}`);
            lines.push(`主营产品类目：${(current?.main_product_categories || []).join('；') || '-'}`);
            lines.push(`销售平台店铺链接或产品链接：${(current?.sales_platform_links || []).join('；') || '-'}`);
            lines.push(`电话国家码：${current?.phone_country_code || '-'}`);
            lines.push(`电话号码：${current?.phone_number || '-'}`);
            lines.push(`已开通业务板块：${(current?.enabled_business_modules || []).join('；') || '-'}`);
            lines.push(`已完成补充验证：${(current?.completed_verifications_labels || []).join('；') || '-'}`);
            const socialText = current?.social_accounts && Object.keys(current.social_accounts).length > 0
              ? Object.entries(current.social_accounts).map(([k, v]) => `${k}: ${v}`).join('；')
              : '-';
            lines.push(`社交媒体：${socialText}`);
            lines.push(`备注：${current?.notes || '-'}`);
            copyText(lines.join('\n'), '客户信息已复制');
          }}
        >
          复制信息
        </Button>,
        <Button key="close" onClick={() => setDetailVisible(false)}>关闭</Button>
      ]}
      width={920}
    >
      <div className="space-y-2">
        <div><b>客户编码：</b><span style={{ color: '#2563eb', fontWeight: 700 }}>{current?.customer_code || '-'}</span></div>
        <div><b>用户名：</b>{current?.username || '-'}</div>
        <div><b>邮箱：</b>{current?.email || '-'}</div>
        <div><b>账号角色：</b>{current?.customer_sub_role === 'main_account' ? '主账号' : current?.customer_sub_role === 'sub_account' ? '子账号' : '-'}</div>
        <div><b>所属主账号：</b>{current?.customer_sub_role === 'sub_account' ? (current?.parent_username || '') : ''}</div>
        <div><b>公司名称：</b>{current?.company_name || '-'}</div>
        <div><b>个人姓名：</b>{current?.personal_name || '-'}</div>
        <div>
          <b>网站：</b>
          {current?.website ? (
            <a href={openExternal(current.website) || '#'} target="_blank" rel="noreferrer">{current.website}</a>
          ) : '-'}
        </div>
        <div>
          <b>主营产品类目：</b>
          {(current?.main_product_categories || []).length > 0 ? (
            <div>
              {(current?.main_product_categories || []).map((item, idx) => (
                <div key={`${item}-${idx}`}>{item}</div>
              ))}
            </div>
          ) : '-'}
        </div>
        <div>
          <b>销售平台店铺链接或产品链接：</b>
          {(current?.sales_platform_links || []).length > 0 ? (
            <div>
              {(current?.sales_platform_links || []).map((item, idx) => {
                const href = openExternal(item);
                return (
                  <div key={`${item}-${idx}`}>
                    {href ? (
                      <a href={href} target="_blank" rel="noreferrer">{item}</a>
                    ) : item}
                  </div>
                );
              })}
            </div>
          ) : '-'}
        </div>
        <div><b>电话国家码：</b>{current?.phone_country_code || '-'}</div>
        <div><b>电话号码：</b>{current?.phone_number || '-'}</div>
        <div><b>已开通业务板块：</b>{(current?.enabled_business_modules || []).join('；') || '-'}</div>
        <div><b>已完成补充验证：</b>{(current?.completed_verifications_labels || []).join('；') || '-'}</div>
        <div><b>社交媒体：</b>{current?.social_accounts && Object.keys(current.social_accounts).length > 0 ? (
          <div>
            {Object.entries(current.social_accounts).map(([k,v]) => (
              <div key={k}>{k}: {v}</div>
            ))}
          </div>
        ) : '-'}</div>
        <div><b>备注：</b>{current?.notes || '-'}</div>
      </div>
    </Modal>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-bold">客户列表</h1>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => fetchPage(pagination.current, pagination.pageSize)}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/dashboard/admin/customers/create')}>
            新建客户
          </Button>
        </Space>
      </div>
      <div className="bg-white p-4 rounded border mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <Input allowClear placeholder="模糊搜索：编码/公司/用户名" value={filters.q} onChange={e => setFilters(prev => ({ ...prev, q: e.target.value || undefined }))} style={{ width: 260 }} />
          <Input allowClear placeholder="客户编码" value={filters.customer_code} onChange={e => setFilters(prev => ({ ...prev, customer_code: e.target.value || undefined }))} style={{ width: 200 }} />
          <Input allowClear placeholder="公司名称" value={filters.company_name} onChange={e => setFilters(prev => ({ ...prev, company_name: e.target.value || undefined }))} style={{ width: 220 }} />
          <Input allowClear placeholder="用户名" value={filters.username} onChange={e => setFilters(prev => ({ ...prev, username: e.target.value || undefined }))} style={{ width: 200 }} />
          <Select
            allowClear
            placeholder="账号角色"
            value={filters.sub_role}
            onChange={(v) => setFilters(prev => ({ ...prev, sub_role: v || undefined }))}
            style={{ width: 160 }}
            options={[
              { label: '主账号', value: 'main_account' },
              { label: '子账号', value: 'sub_account' },
            ]}
          />
          <span style={{ color: '#666' }}>有权限申请</span>
          <Switch
            checked={pendingApplicationOnly}
            onChange={(checked) => setPendingApplicationOnly(checked)}
          />
          <span style={{ color: '#666' }}>显示禁用账号</span>
          <Switch
            checked={filters.is_active !== 'true'}
            onChange={(checked) => setFilters(prev => ({ ...prev, is_active: checked ? undefined : 'true' }))}
          />
          <Button onClick={() => { setFilters({}); fetchPage(1, pagination.pageSize); }}>重置</Button>
          <Button type="primary" onClick={() => fetchPage(1, pagination.pageSize)}>查询</Button>
        </div>
      </div>
      <Table
        rowKey="id"
        loading={loading}
        dataSource={data}
        columns={columns}
        onRow={(record) => (
          fromDashboardPendingReview && hasPendingApplication(record)
            ? { style: { backgroundColor: '#e6f7ff' } }
            : (
              record.is_active === false
                ? { style: { backgroundColor: '#fff1f0' } }
                : {}
            )
        )}
        pagination={pendingApplicationOnly ? false : {
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: [10, 20, 50, 100],
          showTotal: (total, range) => `显示第 ${range[0]} - ${range[1]} 条，共 ${total} 条记录`,
          onChange: (page, pageSize) => fetchPage(page, pageSize),
          onShowSizeChange: (page, pageSize) => fetchPage(1, pageSize),
        }}
        scroll={{ x: 2200 }}
      />
      <DetailModal />
      <Modal
        open={riskVisible}
        title={`风险设置 - ${riskCurrent?.customer_code || riskCurrent?.username || ''}`}
        onCancel={() => setRiskVisible(false)}
        onOk={handleSaveRisk}
        confirmLoading={riskSaving}
        destroyOnClose
        width={520}
      >
        <Form form={riskForm} layout="vertical" disabled={riskLoading}>
          <Form.Item
            name="deposit_multiplier"
            label="保证金乘数"
            tooltip="在物流产品自己配置的保证金比例/最低额/上限基础上整体乘上这个系数，1 表示不调整；
              新客户/风险较高可以调高，长期合作/已认证客户可以调低"
            rules={[{ required: true, message: '请输入保证金乘数' }]}
          >
            <InputNumber min={0} step={0.1} style={{ width: '100%' }} placeholder="默认 1.00" />
          </Form.Item>
          <Form.Item
            name="allow_international"
            label="允许购买国际运单"
            valuePropName="checked"
            tooltip="发货地和目的地不在同一个国家即视为国际运单；默认关闭，确认客户风险可控后再手动开启"
          >
            <Switch />
          </Form.Item>
          <Form.Item name="remark" label="备注">
            <Input.TextArea rows={3} placeholder="比如判断依据、是否已签约/已认证" />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={lastLoginVisible}
        title="最后登录信息"
        onCancel={() => {
          setLastLoginVisible(false);
          setLastLoginRecord(null);
        }}
        footer={[
          <Button
            key="close"
            onClick={() => {
              setLastLoginVisible(false);
              setLastLoginRecord(null);
            }}
          >
            关闭
          </Button>,
        ]}
        width={640}
      >
        <div className="space-y-2">
          <div><b>客户：</b>{lastLoginRecord?.username || lastLoginRecord?.email || '-'}</div>
          <div><b>最后登录时间（当地时间/管理员时间）：</b>{renderDualTimezone(lastLoginRecord?.last_login, lastLoginRecord?.timezone)}</div>
          <div><b>IP地址：</b>{lastLoginRecord?.last_login_ip || '-'}</div>
          <div><b>IP所在地：</b>{lastLoginRecord?.last_login_ip_region || '-'}</div>
        </div>
      </Modal>
      <Modal
        open={applicationVisible}
        title={
          <div className="flex items-center gap-4 pr-10">
            <span>审核服务板块开通申请</span>
            <span className="max-w-[220px] truncate font-normal text-blue-600" style={{ marginLeft: 50, marginRight: 50 }}>
              {current ? (current.username || current.email || '-') : '-'}
            </span>
            <span className="font-normal text-gray-600">
              服务板块：
              {applicationRows.length === 1 ? applicationRows[0].module_label : applicationRows.length > 1 ? '多项' : '-'}
            </span>
          </div>
        }
        onCancel={() => { setApplicationVisible(false); setReviewNote(''); setReviewAction('approve'); }}
        footer={null}
        width={860}
      >
        {applicationRows.length === 0 ? (
          <div>{applicationLoading ? '加载中...' : '暂无待审核申请'}</div>
        ) : (
          <div className="space-y-4">
            {applicationRows.map((row) => (
              <div key={row.id} className="rounded border border-gray-200 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <div className="font-medium">服务板块：{row.module_label}</div>
                  <div className="flex items-center gap-2">
                    <Button size="small" onClick={() => copyText(buildApplicationCopyText(row), '申请信息已复制')}>
                      复制信息
                    </Button>
                    <Tag color={row.status === 'approved' ? 'green' : row.status === 'rejected' ? 'red' : 'orange'}>
                      {row.status_label}
                    </Tag>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 text-sm">
                  <div><b>公司名称：</b>{row.payload?.company_name || '-'}</div>
                  <div>
                    <b>公司网站：</b>
                    {row.payload?.website ? (
                      <a href={openExternal(row.payload.website) || '#'} target="_blank" rel="noreferrer">
                        {row.payload.website}
                      </a>
                    ) : '-'}
                  </div>
                  <div><b>个人姓名：</b>{row.payload?.personal_name || '-'}</div>
                  <div><b>主营产品类目：</b>{(row.payload?.main_product_categories || []).join('；') || '-'}</div>
                  <div>
                    <b>销售平台店铺链接或产品链接：</b>
                    {(row.payload?.sales_platform_links || []).length > 0 ? (
                      <div>
                        {(row.payload?.sales_platform_links || []).map((item, idx) => {
                          const href = openExternal(item);
                          return (
                            <div key={`${item}-${idx}`} style={{ margin: '16px 0' }}>
                              {href ? (
                                <a href={href} target="_blank" rel="noreferrer">{item}</a>
                              ) : item}
                            </div>
                          );
                        })}
                      </div>
                    ) : '-'}
                  </div>
                  <div><b>电话国家码：</b>{row.payload?.phone_country_code || '-'}</div>
                  <div><b>电话号码：</b>{row.payload?.phone_number || '-'}</div>
                  <div>
                    <b>社交媒体账号：</b>
                    {row.payload?.social_accounts && Object.keys(row.payload.social_accounts).length > 0 ? (
                      <span>{Object.entries(row.payload.social_accounts).map(([k, v]) => `${k}:${v}`).join('；')}</span>
                    ) : '-'}
                  </div>
                  {Array.isArray(row.payload?.service_requirement_items) && row.payload.service_requirement_items.length > 0 ? (
                    <div><b>服务需求：</b>{row.payload.service_requirement_items.join(' / ')}</div>
                  ) : null}
                  <div>
                    <b>服务需求补充描述：</b>
                    {String(row.payload?.service_requirement_description || '')
                      .replace(/^服务需求：.*(?:\r?\n)?/, '')
                      .replace(/^补充说明：/, '')
                      .trim() || '-'}
                  </div>
                </div>
                {row.status === 'pending' ? (
                  <div className="mt-3 flex items-center gap-2">
                    <Select
                      style={{ width: 150 }}
                      value={reviewAction}
                      onChange={(v) => setReviewAction(v)}
                      options={[
                        { label: '审核通过', value: 'approve' },
                        { label: '驳回申请', value: 'reject' },
                      ]}
                    />
                    <Input
                      value={reviewNote}
                      onChange={(e) => setReviewNote(e.target.value)}
                      placeholder="审核备注（可选）"
                      allowClear
                    />
                    <Button
                      type={reviewAction === 'approve' ? 'primary' : 'default'}
                      danger={reviewAction === 'reject'}
                      loading={reviewingId === row.id}
                      onClick={() => handleReview(row, reviewAction)}
                    >
                      {reviewAction === 'approve' ? '确认通过' : '确认驳回'}
                    </Button>
                  </div>
                ) : (
                  <div className="mt-3 text-xs text-gray-500">
                    该申请已处理，如需再次申请请由客户在服务板块选择页发起重新申请。
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        title={pwdUser ? `为客户 ${pwdUser.username} 重置密码` : '重置密码'}
        open={pwdVisible}
        onCancel={() => setPwdVisible(false)}
        onOk={() => form.submit()}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical" onFinish={async (vals) => {
          if (!pwdUser) return;
          try {
            if (!vals.password || vals.password.length < 6) {
              message.warning('请输入至少6位的新密码');
              return;
            }
            await request.post(`/api/v1/users/${pwdUser.id}/set_password/`, { password: vals.password });
            message.success('密码已更新');
            setPwdVisible(false);
          } catch (e: any) {
            message.error(e?.response?.data?.detail || '更新失败');
          }
        }}>
          <Form.Item label="新密码" name="password" rules={[{ required: true, message: '请输入新密码' }, { min: 6, message: '至少6位' }]}>
            <Input.Password placeholder="请输入新密码" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminCustomerList;


